/* eslint-disable max-lines */
import { type IllustrationType, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { TIERS_FULL_PATH } from "@src/appV2/redesign/Tiers/paths";
import { ShiftWindow } from "@src/appV2/Shifts/Shift/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { differenceInHours, differenceInMinutes, parseISO } from "date-fns";
import pluralize from "pluralize";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";

import { useGetShiftAccessWindows } from "../useGetShiftAccessWindows";
import { ShiftBottomSheetBookabilityCriteriaCard } from "./BookabilityCriteriaCard";
import { type ShiftBottomSheetInnerCardIntent } from "./InnerCard";
import { useReliableWindowingSequence } from "./useReliableWindowingSequence";

interface WindowContent {
  title: string | React.ReactNode;
  description: React.ReactNode;
}

interface CardConfig {
  illustration: IllustrationType;
  intent?: ShiftBottomSheetInnerCardIntent;
}

interface WindowedOutCardProps {
  windowName: string;
  windowScoreThreshold?: number;
  score?: number;
  shiftStartIso?: string;
  shiftId: string;
  workplaceId: string;
}

export function WindowedOutCard(props: WindowedOutCardProps) {
  const history = useHistory();

  const { minVerifiedShiftsForFamiliarity, daysToEvaluateFamiliarity } =
    useReliableWindowingSequence(props.workplaceId);

  const worker = useDefinedWorker();

  // if the user score is higher, than the threshold do not show error message for CBH score strats, suggesting that the CBH score is too low
  const showGenericMessage =
    isDefined(props.score) &&
    isDefined(props.windowScoreThreshold) &&
    props.score >= props.windowScoreThreshold;

  const showWindowThreshold =
    isDefined(props.score) &&
    isDefined(props.windowScoreThreshold) &&
    props.score < props.windowScoreThreshold;

  // Fetch access windows for the shift
  const { data: accessWindowsData } = useGetShiftAccessWindows(
    { shiftId: props.shiftId },
    { enabled: props.windowName === ShiftWindow.TIERS }
  );

  const tiersEndAt = useMemo(() => {
    return accessWindowsData?.data?.find((w) => w.attributes.windowName === "TIERS")?.attributes
      .endAt;
  }, [accessWindowsData]);

  const estimation = useMemo(() => {
    const leadTimeInHours = props.shiftStartIso
      ? differenceInHours(parseISO(props.shiftStartIso), new Date())
      : undefined;

    if (!leadTimeInHours) {
      return "in a few minutes!";
    }

    if (leadTimeInHours > 24 * 7) {
      return "later today!";
    }

    if (leadTimeInHours > 24) {
      return "in a couple hours!";
    }

    return "in a few minutes!";
  }, [props.shiftStartIso]);

  const minutesRemaining = useMemo(() => {
    return isDefined(tiersEndAt) ? differenceInMinutes(parseISO(tiersEndAt), new Date()) : 0;
  }, [tiersEndAt]);

  /* eslint-disable complexity, sonarjs/cognitive-complexity */
  const windowContent = useMemo((): WindowContent => {
    const isClipboardScoreWindow =
      props.windowName === ShiftWindow.CLIPBOARD_SCORE_FIRST ||
      props.windowName === ShiftWindow.CLIPBOARD_SCORE_SECOND;

    if (isClipboardScoreWindow && !showWindowThreshold) {
      logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_SCORING_WINDOW_DONT_SHOW_THRESHOLD, {
        workerId: worker._id,
        msa: worker.address?.metropolitanStatisticalArea,
        shiftId: props.shiftId,
        windowName: props.windowName,
        showWindowThreshold,
        userScore: props.score,
        windowScoreThreshold: props.windowScoreThreshold,
      });
    }

    if (props.windowName === ShiftWindow.A_TEAM) {
      return {
        title: `You can't book yet. Check back ${estimation}`,
        description:
          "The shift is being offered first to the workplace's curated list of top professionals.",
      };
    }

    if (props.windowName === ShiftWindow.RELIABLE_AND_FAMILIAR) {
      const isScoreAboveThreshold =
        isDefined(props.score) &&
        isDefined(props.windowScoreThreshold) &&
        props.score >= props.windowScoreThreshold;

      const description = isScoreAboveThreshold
        ? `This shift is being offered first to professionals who have worked at least ${minVerifiedShiftsForFamiliarity} ${pluralize(
            "shift",
            minVerifiedShiftsForFamiliarity
          )} at this workplace in the last ${daysToEvaluateFamiliarity} days at this workplace.`
        : `This shift is being offered first to professionals who have recently worked here and have ${
            showWindowThreshold
              ? `at least ${props.windowScoreThreshold} pts`
              : "a high Clipboard Score"
          }.`;

      return {
        title: `You can't book yet. Check back ${estimation}`,
        description: (
          <Stack spacing={4}>
            <Box>{description}</Box>
            {isDefined(props.score) && !isScoreAboveThreshold && (
              <Text color={(theme) => theme.palette.text.secondary} variant="body2">
                You currently have {props.score} pts.
              </Text>
            )}
          </Stack>
        ),
      };
    }

    if (isClipboardScoreWindow && !showGenericMessage) {
      return {
        title: `You can't book yet. Check back ${estimation}`,
        description: (
          <Stack spacing={4}>
            <Box>
              This shift is being offered first to professionals with{" "}
              {showWindowThreshold
                ? `at least ${props.windowScoreThreshold} pts`
                : "a high Clipboard Score"}
              .
            </Box>
            {isDefined(props.score) && (
              <Text color={(theme) => theme.palette.text.secondary} variant="body2">
                You currently have {props.score} pts.
              </Text>
            )}
          </Stack>
        ),
      };
    }

    if (props.windowName === ShiftWindow.FAMILIAR_WORKERS) {
      return {
        title: `You can't book yet. Check back ${estimation}`,
        description: (
          <Stack spacing={4}>
            <Box>
              This shift is being offered first to those who have recently worked here and have{" "}
              {showWindowThreshold
                ? `at least ${props.windowScoreThreshold} pts`
                : "a high Clipboard Score"}
              .
            </Box>
            {isDefined(props.score) && (
              <Text color={(theme) => theme.palette.text.secondary} variant="body2">
                You currently have {props.score} pts.
              </Text>
            )}
          </Stack>
        ),
      };
    }

    if (props.windowName === ShiftWindow.TIERS) {
      return {
        title: (
          <Text inline semibold variant="h5">
            <Text inline semibold variant="h5">
              This shift is exclusive to{" "}
            </Text>
            <Text
              inline
              semibold
              variant="h5"
              sx={(theme) => ({ color: theme.palette.tiers?.gold?.text })}
            >
              Gold
            </Text>
            <Text inline semibold variant="h5">
              {" "}
              members{" "}
            </Text>
            {minutesRemaining > 0 && (
              <Text inline semibold variant="h5">
                for the next {minutesRemaining} {pluralize("minute", minutesRemaining)}
              </Text>
            )}
          </Text>
        ),
        description: "Become a Gold member to get first dibs on every shift on Clipboard.",
      };
    }

    return {
      title: "Unavailable",
      description: "This shift is not available for you at this time.",
    };
  }, [
    props.windowName,
    props.score,
    props.shiftId,
    props.windowScoreThreshold,
    showGenericMessage,
    estimation,
    minVerifiedShiftsForFamiliarity,
    daysToEvaluateFamiliarity,
    showWindowThreshold,
    minutesRemaining,
    worker._id,
    worker.address?.metropolitanStatisticalArea,
  ]);
  /* eslint-enable complexity, sonarjs/cognitive-complexity */

  const cardConfig = useMemo((): CardConfig => {
    return props.windowName === ShiftWindow.TIERS
      ? {
          illustration: "tiers-windowed-out",
          intent: "tiers",
        }
      : {
          illustration: "hour-glass",
        };
  }, [props.windowName]);

  const { title, description } = windowContent;
  const { illustration, intent } = cardConfig;

  const footer = useMemo(() => {
    if (props.windowName === ShiftWindow.TIERS) {
      return (
        <Button
          size="medium"
          role="button"
          sx={{ width: "100%" }}
          onClick={() => {
            history.push(TIERS_FULL_PATH);
            logEvent(APP_V2_USER_EVENTS.TIERS_BOOKING_CARD_LEARN_MORE_CLICKED);
          }}
        >
          Learn More
        </Button>
      );
    }

    return null;
  }, [history, props.windowName]);

  return (
    <ShiftBottomSheetBookabilityCriteriaCard
      title={title}
      description={description}
      illustrationType={illustration}
      footer={footer}
      intent={intent}
    />
  );
}
/* eslint-enable max-lines */
